import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RotateCcw, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const DEFAULTS = {
  hero_headline: "Words written\n_in faith,_ for\nhearts that *listen.*",
  hero_body:
    "I'm Barbara Ruffing — author of my very first children's book, and a quiet collector of poems and essays that encourage individuals on their walk of faith.",
  hero_cta_primary_label: "Read the Writings",
  hero_cta_secondary_label: "About Barbara",
  featured_book_kicker: "Now Available · First Children's Book",
  featured_book_cta_label: "Discover the book",
  latest_kicker: "The Latest",
  latest_heading: "From the writing desk",
  section_order: ["slideshow", "book", "hero", "writings", "listen", "missions"],
  text_scale: "default",
  video_url: "",
  video_caption: "",
  video_kicker: "Watch",
};

const SECTION_LABEL = {
  hero: "Hero (your photo + headline)",
  book: "The Book (cover + title + buy link)",
  writings: "Latest Writings (recent 3 posts)",
  listen: "Latest Song (newest featured song)",
  missions: "Globally Minded! (Missions section)",
  slideshow: "Slideshow (mission photos — move it anywhere)",
};

const FIELDS = [
  {
    key: "hero_headline",
    label: "Main Headline",
    type: "textarea",
    rows: 4,
    serif: true,
    help: "Use *asterisks* to highlight a word in terracotta. Use _underscores_ to italicize. Newlines become new lines on the page.",
  },
  {
    key: "hero_body",
    label: "Intro Paragraph (under headline)",
    type: "textarea",
    rows: 4,
  },
  {
    key: "hero_cta_primary_label",
    label: "Primary Button Label",
    type: "text",
  },
  {
    key: "hero_cta_secondary_label",
    label: "Secondary Button Label",
    type: "text",
  },
  {
    key: "featured_book_kicker",
    label: "Featured-Book Section Kicker (small green text)",
    type: "text",
  },
  {
    key: "featured_book_cta_label",
    label: "Featured-Book Link Label",
    type: "text",
  },
  {
    key: "latest_kicker",
    label: "Latest Writings Kicker",
    type: "text",
  },
  {
    key: "latest_heading",
    label: "Latest Writings Heading",
    type: "text",
    serif: true,
  },
  {
    key: "video_url",
    label: "Home Video URL (YouTube — leave blank to hide the video section)",
    type: "text",
    placeholder: "https://youtube.com/shorts/... or https://youtu.be/...",
    help: "Paste any YouTube link (Shorts, Watch page, or share link). The video shows near the top of the home page. Clear this field to hide it.",
  },
  {
    key: "video_kicker",
    label: "Video Section Kicker (small green text above the video)",
    type: "text",
    placeholder: "Watch",
  },
  {
    key: "video_caption",
    label: "Video Caption (optional line shown below the video)",
    type: "textarea",
    rows: 2,
  },
];

export default function AdminHomepage() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/homepage")
      .then((r) => {
        const data = r.data || {};
        const order = Array.isArray(data.section_order) && data.section_order.length
          ? data.section_order.filter((s) => SECTION_LABEL[s])
          : DEFAULTS.section_order;
        // Make sure all sections are present
        DEFAULTS.section_order.forEach((s) => {
          if (!order.includes(s)) order.push(s);
        });
        setForm({ ...DEFAULTS, ...data, section_order: order, text_scale: data.text_scale || "default" });
      })
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const resetField = (k) => setForm((f) => ({ ...f, [k]: DEFAULTS[k] }));

  const moveSection = (idx, dir) =>
    setForm((f) => {
      const next = [...(f.section_order || DEFAULTS.section_order)];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return f;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...f, section_order: next };
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/homepage", form);
      toast.success("Home page saved");
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20" data-testid="admin-homepage-loading">
        <p className="text-ink-soft">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-homepage-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-homepage-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-4">
          Home Page
        </p>
        <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
          Edit your front page
        </h1>
        <p className="mt-3 text-sm text-ink-soft max-w-2xl">
          Everything you can read on the home page is editable here. The
          headline supports a tiny markup: <code className="text-ink">*highlight*</code> turns a word terracotta, <code className="text-ink">_italic_</code> italicizes it.
        </p>

        <div className="mt-12 space-y-8">
          <div className="border border-rule p-5 bg-paper-2/40">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs uppercase tracking-[0.2em] text-ink-soft">
                Overall Text Size
              </p>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, text_scale: "default" }))}
                data-testid="admin-homepage-reset-text-scale"
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-terracotta"
              >
                <RotateCcw size={12} /> Reset
              </button>
            </div>
            <p className="text-xs text-ink-soft italic mb-4">
              Scales every heading, paragraph, and spacing across the entire site proportionally. Useful if your laptop screen makes the site feel a bit large or a bit small.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="admin-homepage-text-scale">
              {[
                { v: "small", label: "Small" },
                { v: "default", label: "Default" },
                { v: "comfortable", label: "Comfortable" },
                { v: "large", label: "Large" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, text_scale: opt.v }))}
                  data-testid={`admin-homepage-text-scale-${opt.v}`}
                  className={`px-4 py-3 border text-xs tracking-[0.18em] uppercase transition-colors ${
                    form.text_scale === opt.v
                      ? "bg-ink text-paper border-ink"
                      : "bg-paper border-rule text-ink-soft hover:text-ink hover:border-ink"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-rule p-5 bg-paper-2/40">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs uppercase tracking-[0.2em] text-ink-soft">
                Section Order
              </p>
              <button
                type="button"
                onClick={() =>
                  setForm((f) => ({ ...f, section_order: DEFAULTS.section_order }))
                }
                data-testid="admin-homepage-reset-section-order"
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-terracotta"
              >
                <RotateCcw size={12} /> Reset
              </button>
            </div>
            <p className="text-xs text-ink-soft italic mb-4">
              Drag isn't supported here — use the arrows to move sections up or down. The order applies to the public Home page.
            </p>
            <ul className="space-y-2" data-testid="admin-homepage-section-list">
              {(form.section_order || DEFAULTS.section_order).map((s, i, arr) => (
                <li
                  key={s}
                  data-testid={`admin-homepage-section-${s}`}
                  className="flex items-center gap-3 bg-paper border border-rule px-4 py-3"
                >
                  <span className="font-serif-display text-lg text-ink flex-1">
                    <span className="text-ink-soft mr-2 text-sm tabular-nums">
                      {i + 1}.
                    </span>
                    {SECTION_LABEL[s] || s}
                  </span>
                  <button
                    type="button"
                    onClick={() => moveSection(i, -1)}
                    disabled={i === 0}
                    data-testid={`admin-homepage-move-up-${s}`}
                    className="p-2 border border-rule text-ink-soft hover:text-ink hover:border-ink disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveSection(i, 1)}
                    disabled={i === arr.length - 1}
                    data-testid={`admin-homepage-move-down-${s}`}
                    className="p-2 border border-rule text-ink-soft hover:text-ink hover:border-ink disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ChevronDown size={16} />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {FIELDS.map((f) => (
            <div key={f.key}>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft">
                  {f.label}
                </label>
                <button
                  type="button"
                  onClick={() => resetField(f.key)}
                  data-testid={`admin-homepage-reset-${f.key}`}
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-terracotta"
                  title="Reset to default"
                >
                  <RotateCcw size={12} /> Reset
                </button>
              </div>
              {f.type === "textarea" ? (
                <textarea
                  rows={f.rows || 3}
                  value={form[f.key]}
                  onChange={set(f.key)}
                  placeholder={f.placeholder || ""}
                  data-testid={`admin-homepage-${f.key}`}
                  className={`w-full bg-paper border border-rule px-4 py-3 text-ink leading-relaxed outline-none focus:border-ink ${
                    f.serif
                      ? "font-serif-display text-2xl leading-snug"
                      : "text-base"
                  }`}
                />
              ) : (
                <input
                  value={form[f.key]}
                  onChange={set(f.key)}
                  placeholder={f.placeholder || ""}
                  data-testid={`admin-homepage-${f.key}`}
                  className={`w-full bg-transparent border-b border-rule focus:border-ink py-3 outline-none text-ink ${
                    f.serif
                      ? "font-serif-display text-2xl"
                      : "text-sm"
                  }`}
                />
              )}
              {f.help && (
                <p className="mt-2 text-xs text-ink-soft italic leading-relaxed">
                  {f.help}
                </p>
              )}
            </div>
          ))}

          <div className="pt-6 border-t border-rule flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-homepage-save-btn"
              className="px-6 py-3 bg-terracotta text-paper text-xs tracking-[0.18em] uppercase hover:bg-ink transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Home Page"}
            </button>
            <Link
              to="/"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-homepage-preview-link"
              className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-terracotta link-underline"
            >
              Preview ↗
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
